/**
 * AdminApiClient v2 - Clean, thin fetch-based client for admin RPC gateway
 * 
 * Key improvements:
 * - Direct fetch to /functions/v1/admin (no Supabase SDK overhead)
 * - Proper timeout/abort with AbortController
 * - Fail-closed: requires valid session unless using callIfSession
 * - Public surface protection: blocks calls outside admin areas unless allowOnPublic
 * - Simple call() interface with strict error handling
 * - Compatible with existing error types and telemetry
 */

import { supabase } from '@/integrations/supabase/client';
import { isAdminSurface } from '@/lib/routing/isAdminSurface';
import { resolveLegacyAdminAct } from '../../../config/legacy-admin-map';
import { getInternalEdgeSecret } from './internal-secret';
import { isWebhookEndpoint, webhooksPublicClient } from './webhooks-client';

const CANONICAL_ADMIN_ACTS = new Set<string>([
  'dashboard.totals',
  'customers.list',
  'customers.count',
  'activity.recent.transactions',
  'activity.recent.emails',
  'observability.metrics.synthetic',
  'observability.alertRules.synthetic',
  'observability.events.list',
  'webhooks.recent',
  'diagnostics.snapshot',
  'webhooks.security.summary',
  'webhooks.stats',
  'ops.webhooks.replay',
  'security.rateLimit.stats',
  'security.events.search',
  'ops.transactions.list',
  'ops.transactions.export',
  'waitlist.list',
  'wallet.externalAccounts.list',
]);

const LEGACY_TO_CANONICAL_ACT: Record<string, string> = {
  // Dashboard & Health
  get_system_health_metrics: 'dashboard.totals',
  get_onboarding_health_metrics_safe: 'dashboard.totals',

  // Customers
  admin_list_customers_unified: 'customers.list',
  admin_unified_customers_list: 'customers.list',
  admin_people_count: 'customers.count',

  // Activity & Metrics
  admin_activity_metrics: 'dashboard.totals',
  admin_recent_transactions: 'activity.recent.transactions',
  admin_recent_payouts: 'activity.recent.transactions',
  admin_recent_email_queue: 'activity.recent.emails',
  admin_recent_onboarding_events: 'activity.recent.transactions',
  admin_recent_webhooks: 'webhooks.recent',
  admin_list_webhook_events_with_audit: 'webhooks.recent',
  admin_pending_operations_alerts: 'diagnostics.snapshot',
  admin_get_system_metrics: 'observability.metrics.synthetic',
  admin_list_alert_rules: 'observability.alertRules.synthetic',

  // Security & Webhooks
  admin_webhook_security_summary: 'webhooks.security.summary',
  admin_webhooks_stats: 'webhooks.stats',
  admin_bridge_webhook_replay: 'ops.webhooks.replay',

  // Wallet & Banking
  get_user_bank_accounts: 'wallet.externalAccounts.list',
};

export function resolveAdminHubAct(functionName: string): string | null {
  if (CANONICAL_ADMIN_ACTS.has(functionName)) {
    return functionName;
  }

  // Check legacy-to-canonical mapping first (local)
  const mapped = LEGACY_TO_CANONICAL_ACT[functionName];
  if (mapped && CANONICAL_ADMIN_ACTS.has(mapped)) {
    return mapped;
  }

  // Check legacy-admin-map.ts (shared with Edge Function)
  try {
    const legacyMapped = resolveLegacyAdminAct(functionName);

    if (legacyMapped && CANONICAL_ADMIN_ACTS.has(legacyMapped)) {
      return legacyMapped;
    }

    // Also check if legacyMapped is a valid act (even if not in CANONICAL_ADMIN_ACTS)
    // This allows new acts to be added without updating CANONICAL_ADMIN_ACTS immediately
    if (legacyMapped) {
      return legacyMapped;
    }
  } catch (err) {
    console.error('[resolveAdminHubAct] Error calling resolveLegacyAdminAct:', err);
  }

  return null;
}

export function isAdminHubFunction(functionName: string): boolean {
  return resolveAdminHubAct(functionName) !== null;
}

export async function callAdmin<TResult = unknown, TBody = unknown>(act: string, body?: TBody): Promise<TResult> {
  const url = `/functions/v1/admin?act=${encodeURIComponent(act)}&v=1`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-RPC-Function': act,
    },
    body: JSON.stringify(body ?? {}),
  });

  if (!res.ok) {
    throw new Error(`Admin act failed: ${act} (${res.status})`);
  }

  return (await res.json()) as TResult;
}

const nowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

type SensitiveDescriptor = { present: true; length: number; checksum: string } | { present: false };

function checksum(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function describeSensitive(value: string | undefined): SensitiveDescriptor {
  if (!value) return { present: false };
  const trimmed = value.trim();
  if (!trimmed) return { present: false };
  return {
    present: true,
    length: trimmed.length,
    checksum: checksum(trimmed),
  };
}

function describeAuthorization(value: string | undefined) {
  if (!value) return { present: false as const };
  const trimmed = value.trim();
  if (!trimmed) return { present: false as const };
  const [scheme, token = ''] = trimmed.split(' ', 2);
  return {
    present: true as const,
    scheme: scheme || 'unknown',
    tokenLength: token.length,
    tokenChecksum: token ? checksum(token) : undefined,
  };
}

function buildHeaderDiagnostics(headers: Record<string, string>) {
  return {
    authorization: describeAuthorization(headers['authorization']),
    internalSecret: describeSensitive(headers['x-internal-secret']),
    idempotencyKey: describeSensitive(headers['idempotency-key']),
    traceId: headers['x-trace-id'],
    clientTag: headers['x-client'],
  };
}

function readResponseHeader(headers: unknown, name: string): string | null {
  if (!headers) return null;

  const headerLike = headers as { get?: (key: string) => unknown };
  if (typeof headerLike.get !== 'function') {
    return null;
  }

  try {
    const value = headerLike.get(name);
    return typeof value === 'string' ? value : value ?? null;
  } catch {
    return null;
  }
}

// Error codes aligned with UI expectations
export const AdminApiErrorCodes = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',
  TIMEOUT: 'TIMEOUT',
  NETWORK_ERROR: 'NETWORK_ERROR',
  RETRYABLE: 'RETRYABLE',
  SERVER_ERROR: 'SERVER_ERROR',
  MISSING_INTERNAL_SECRET: 'MISSING_INTERNAL_SECRET',
  BLOCKED_ON_PUBLIC: 'BLOCKED_ON_PUBLIC', // New: blocked on public surface
} as const;

export type AdminApiErrorCode = keyof typeof AdminApiErrorCodes;

function mapHttpStatusToErrorCode(status: number): AdminApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 400 || status === 422) return 'BAD_REQUEST';
  if (status === 409 || status === 425 || status === 429) return 'RETRYABLE';
  if (status >= 500) return 'RETRYABLE';
  return 'SERVER_ERROR';
}

function isRetriableError(code: AdminApiErrorCode): boolean {
  return code === 'RETRYABLE' || code === 'NETWORK_ERROR' || code === 'TIMEOUT';
}

// SSR-safe UUID generation
function generateTraceId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeEdgePayload<TResult>(raw: unknown): TResult {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'data' in (raw as Record<string, unknown>)) {
    const container = raw as { data: unknown; _meta?: unknown };
    const inner = container.data as TResult;

    if (container._meta !== undefined && inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return { ...(inner as Record<string, unknown>), _meta: container._meta } as TResult;
    }

    return inner;
  }

  return raw as TResult;
}

export type ApiV2Result<TResult = unknown> =
  | { ok: true; data: TResult; traceId: string }
  | { ok: false; code: AdminApiErrorCode; error: string; traceId: string; details?: unknown };

type TPayload = Record<string, unknown> | undefined;

export interface CallOptions {
  timeoutMs?: number;
  retryCount?: number;
  idempotencyKey?: string;
  token?: string; // Override token (for service calls)
  allowOnPublic?: boolean; // Allow call on public surfaces (bypass protection)  
}

export class AdminApiClientV2 {
  private readonly baseUrl: string;
  private readonly defaultTimeoutMs: number;
  private readonly supabaseUrl: string;

  constructor() {
    // Point directly to admin gateway (migrated from admin-rpc)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL environment variable is required');
    }
    // Directly target admin edge function instead of legacy /api/admin proxy
    this.baseUrl = `${supabaseUrl}/functions/v1/admin`;
    this.defaultTimeoutMs = 8000; // Tighter timeout for better UX
    this.supabaseUrl = supabaseUrl;
  }

  /**
   * Strict call - requires valid session, fails fast if no auth
   * Blocks calls on public surfaces unless explicitly allowed
   */
  async call<TPayload = any, TResult = any>(
    functionName: string,
    payload?: TPayload,
    options: CallOptions = {}
  ): Promise<ApiV2Result<TResult>> {
    // Public surface protection - prevent admin calls outside admin areas
    if (import.meta.env.VITE_DISABLE_ADMIN_API_ON_PUBLIC === 'true'
      && !options.allowOnPublic
      && !isAdminSurface()) {
      return {
        ok: false,
        code: 'BLOCKED_ON_PUBLIC',
        error: 'Admin API calls are disabled on public surfaces',
        traceId: generateTraceId()
      };
    }

    const adminAct = resolveAdminHubAct(functionName);

    if (!adminAct) {
      // Try direct RPC first (for functions like admin_customer_overview)
      return this.callSupabaseRpc<TResult>(functionName, payload ?? {}, generateTraceId());
    }

    // Check if this is a webhook endpoint and use public client to avoid CORS issues
    if (isWebhookEndpoint(adminAct)) {
      return this.callWebhooksPublic<TResult>(adminAct, payload ?? {});
    }

    // Obtain access token for authenticated admin requests
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      return {
        ok: false,
        code: 'UNAUTHORIZED',
        error: 'No valid session found',
        traceId: generateTraceId()
      };
    }
    const token = options.token || session?.access_token;
    if (!token) {
      return {
        ok: false,
        code: 'UNAUTHORIZED',
        error: 'No valid session found',
        traceId: generateTraceId()
      };
    }

    return this.makeRequest<TResult>(adminAct, payload ?? {}, token, options);
  }

  /**
   * Soft call - returns error if no session (consistent result type)
   */
  async callIfSession<TPayload = any, TResult = any>(
    functionName: string,
    payload?: TPayload,
    options: CallOptions = {}
  ): Promise<ApiV2Result<TResult>> {
    const adminAct = resolveAdminHubAct(functionName);

    if (!adminAct) {
      // Try direct RPC first (for functions like admin_customer_overview)
      return this.callSupabaseRpc<TResult>(functionName, payload ?? {}, generateTraceId());
    }

    // Check if this is a webhook endpoint and use public client to avoid CORS issues
    if (isWebhookEndpoint(adminAct)) {
      return this.callWebhooksPublic<TResult>(adminAct, payload ?? {});
    }

    const { data: { session } } = await supabase.auth.getSession();
    const token = options.token || session?.access_token;

    if (!token) {
      return {
        ok: false,
        code: 'UNAUTHORIZED',
        error: 'No session available',
        traceId: generateTraceId()
      };
    }

    return this.makeRequest<TResult>(adminAct, payload ?? {}, token, options);
  }

  private async makeRequest<TResult>(
    adminAct: string, // NOTE: This is already the mapped act (e.g., 'wallet.externalAccounts.list'), not the original function name
    payload: Record<string, unknown>,
    token: string,
    options: CallOptions
  ): Promise<ApiV2Result<TResult>> {
    // Double safety: block at makeRequest level too (prevents direct bypasses)
    if (import.meta.env.VITE_DISABLE_ADMIN_API_ON_PUBLIC === 'true'
      && !options.allowOnPublic
      && !isAdminSurface()) {
      return {
        ok: false,
        code: 'BLOCKED_ON_PUBLIC',
        error: 'Admin API calls are disabled on public surfaces',
        traceId: generateTraceId()
      };
    }

    const traceId = generateTraceId();
    const timeoutMs = options.timeoutMs || this.defaultTimeoutMs;
    const retryCount = options.retryCount ?? 0;

    let lastError: ApiV2Result<TResult> | null = null;

    const isAdminCheck = adminAct === 'is_admin_simple';

    // NOTE: adminAct is already the mapped canonical act (e.g., 'wallet.externalAccounts.list')
    // No need to resolve again - this was causing the bug where resolved acts were re-resolved and returned null

    // CONDITIONAL BYPASS: Check if recursion fix is working
    // Remove is_admin_simple from admin Edge Function ALLOWED_FUNCTIONS to prevent recursion
    const USE_EMERGENCY_BYPASS = import.meta.env.VITE_ADMIN_EMERGENCY_BYPASS === 'true';

    if (USE_EMERGENCY_BYPASS) {
      // Emergency bypass should not be used for mapped acts - this is a fallback for legacy functions
      // If we're here with a mapped act, something is wrong
      console.warn(`[AdminApiClientV2] Emergency bypass requested for mapped act ${adminAct} - this should not happen`);
      return {
        ok: false,
        code: 'SERVER_ERROR',
        error: 'Emergency bypass not available for mapped acts',
        traceId,
        details: { act: adminAct, bypass: 'not_applicable' }
      };
    }

    // NOTE: The rest of this function is now unreachable until bypass is removed
    // This entire Edge Function flow is disabled to prevent admin-guard recursion

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      // Add retry delay for subsequent attempts
      if (attempt > 0) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000); // Exponential backoff, max 4s
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const attemptStartedAt = isAdminCheck ? nowMs() : 0;
      let headerDiagnostics: ReturnType<typeof buildHeaderDiagnostics> | null = null;

      try {
        const internalSecret = getInternalEdgeSecret();
        if (!internalSecret) {
          console.error('[AdminApiClient] Missing VITE_INTERNAL_EDGE_SECRET - admin functions require internal secret');
          if (isAdminCheck) {
            console.error('[AdminApiClientV2][is_admin_simple] internal secret unavailable during request', {
              attempt: attempt + 1,
              traceId,
            });
          }
          return {
            ok: false,
            code: 'MISSING_INTERNAL_SECRET',
            error: 'Internal configuration error - missing admin secret',
            traceId,
            details: { attempt: attempt + 1 }
          };
        }

        // Build headers consistently
        const headers: Record<string, string> = {
          'content-type': 'application/json',
          'authorization': `Bearer ${token}`,
          'x-rpc-function': adminAct, // Use the mapped act, not original function name
          'x-client': 'admin-panel@web',
          'x-trace-id': traceId,
          'x-internal-secret': internalSecret,
        };

        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (anonKey) headers['apikey'] = anonKey;
        if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

        if (isAdminCheck) {
          headerDiagnostics = buildHeaderDiagnostics(headers);
          console.info('[AdminApiClientV2][is_admin_simple] request.start', {
            attempt: attempt + 1,
            traceId,
            timeoutMs,
            retryCount,
            headers: headerDiagnostics,
            payloadSize: payload ? JSON.stringify(payload).length : 0,
          });
        }

        const url = new URL(this.baseUrl);
        url.searchParams.set('act', adminAct);

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers,
          body: JSON.stringify(payload ?? {}),
          signal: controller.signal
        });

        if (isAdminCheck) {
          const durationMs = Math.round((nowMs() - attemptStartedAt) * 1000) / 1000;
          const responseHeaders = (response as any)?.headers;
          console.info('[AdminApiClientV2][is_admin_simple] response.received', {
            attempt: attempt + 1,
            traceId,
            status: response.status,
            ok: response.ok,
            durationMs,
            supabaseRequestId:
              readResponseHeader(responseHeaders, 'x-request-id') ||
              readResponseHeader(responseHeaders, 'x-supabase-request-id') ||
              readResponseHeader(responseHeaders, 'x-trace-id'),
            cfRay: readResponseHeader(responseHeaders, 'cf-ray'),
            rateLimitRemaining:
              readResponseHeader(responseHeaders, 'ratelimit-remaining') ||
              readResponseHeader(responseHeaders, 'x-ratelimit-remaining'),
            rateLimitReset:
              readResponseHeader(responseHeaders, 'ratelimit-reset') ||
              readResponseHeader(responseHeaders, 'x-ratelimit-reset'),
          });
        }

        if (response.ok) {
          // DEFENSIVE JSON PARSING - prevent "Unexpected token" errors
          let raw: unknown;
          try {
            const responseText = await response.text();
            if (!responseText.trim()) {
              console.warn(`[AdminApiClientV2] Empty response for ${adminAct}`);
              raw = null;
            } else {
              raw = JSON.parse(responseText);
            }
          } catch (parseError: any) {
            console.error(`[AdminApiClientV2] JSON parse failed for ${adminAct}:`, {
              error: parseError.message,
              responsePreview: (await response.text()).slice(0, 200)
            });
            return {
              ok: false,
              code: 'SERVER_ERROR',
              error: `Invalid JSON response from server`,
              traceId,
              details: { parseError: parseError.message }
            };
          }

          if (isAdminCheck) {
            console.info('[AdminApiClientV2][is_admin_simple] response.success', {
              attempt: attempt + 1,
              traceId,
              durationMs: Math.round((nowMs() - attemptStartedAt) * 1000) / 1000,
            });
          }
          return {
            ok: true,
            data: normalizeEdgePayload<TResult>(raw),
            traceId
          };
        }

        // Handle HTTP error responses with defensive parsing
        const errorCode = mapHttpStatusToErrorCode(response.status);
        let errorMessage = `HTTP ${response.status}`;

        try {
          const errorText = await response.text();
          if (!errorText.trim()) {
            errorMessage = response.statusText || errorMessage;
          } else {
            try {
              const errorBody = JSON.parse(errorText);
              errorMessage = errorBody.error?.message || errorBody.message || errorMessage;
            } catch {
              // Not JSON - likely HTML error page or plain text
              const preview = errorText.slice(0, 100);
              console.warn(`[AdminApiClientV2] Non-JSON error response for ${adminAct}:`, {
                status: response.status,
                preview,
                isHtml: errorText.trim().startsWith('<')
              });
              errorMessage = `Server error (${response.status})`;
            }
          }
        } catch {
          // Failed to read response text at all
          errorMessage = response.statusText || errorMessage;
        }

        const errorResult: ApiV2Result<TResult> = {
          ok: false,
          code: errorCode,
          error: errorMessage,
          traceId,
          details: { status: response.status, attempt: attempt + 1 }
        };

        if (isAdminCheck) {
          console.warn('[AdminApiClientV2][is_admin_simple] response.error', {
            attempt: attempt + 1,
            traceId,
            status: response.status,
            code: errorCode,
            durationMs: Math.round((nowMs() - attemptStartedAt) * 1000) / 1000,
            headers: headerDiagnostics,
          });
        }

        // Check if we should retry
        if (attempt < retryCount && this.shouldRetryError(errorCode, response.status)) {
          lastError = errorResult;
          continue;
        }

        return errorResult;

      } catch (error: any) {
        const isAborted = error.name === 'AbortError';
        const errorCode = isAborted ? 'TIMEOUT' : 'NETWORK_ERROR';
        const errorMessage = isAborted ? 'Request timed out' : (error.message || 'Network request failed');

        if (isAdminCheck) {
          console.error('[AdminApiClientV2][is_admin_simple] request.exception', {
            attempt: attempt + 1,
            traceId,
            aborted: isAborted,
            durationMs: Math.round((nowMs() - attemptStartedAt) * 1000) / 1000,
            errorName: error?.name,
            errorMessage,
            headers: headerDiagnostics,
          });
        }

        const errorResult: ApiV2Result<TResult> = {
          ok: false,
          code: errorCode,
          error: errorMessage,
          traceId,
          details: { attempt: attempt + 1, originalError: error.name }
        };

        // Retry network errors and timeouts
        if (attempt < retryCount && isRetriableError(errorCode)) {
          lastError = errorResult;
          continue;
        }

        return errorResult;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    // All retries exhausted
    return lastError!;
  }

  private async callLegacyEdge<TResult>(
    functionName: string,
    payload: Record<string, unknown>,
    token: string,
    options: CallOptions
  ): Promise<ApiV2Result<TResult>> {
    const traceId = generateTraceId();
    const timeoutMs = options.timeoutMs || this.defaultTimeoutMs;
    const retryCount = options.retryCount ?? 0;

    let lastError: ApiV2Result<TResult> | null = null;

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      if (attempt > 0) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const headers: Record<string, string> = {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
          'x-client': 'admin-panel@web',
          'x-trace-id': traceId,
        };

        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (anonKey) headers['apikey'] = anonKey;
        if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

        // eslint-disable-next-line internal/no-direct-edge-fetch -- Core AdminApiClient: this IS the abstraction layer
        const response = await fetch(`${this.supabaseUrl}/functions/v1/${functionName}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload ?? {}),
          signal: controller.signal,
        });

        if (response.ok) {
          let raw: unknown = null;
          const responseText = await response.text();
          if (responseText.trim()) {
            try {
              raw = JSON.parse(responseText);
            } catch (parseError: any) {
              console.error(
                `[AdminApiClientV2] Legacy edge JSON parse failed for ${functionName}:`,
                parseError?.message ?? parseError
              );
              return {
                ok: false,
                code: 'SERVER_ERROR',
                error: 'Invalid JSON response from legacy edge function',
                traceId,
                details: { function: functionName, attempt: attempt + 1 },
              };
            }
          }

          return {
            ok: true,
            data: raw as TResult,
            traceId,
          };
        }

        const errorCode = mapHttpStatusToErrorCode(response.status);
        let errorMessage = response.statusText || `HTTP ${response.status}`;

        try {
          const errorText = await response.text();
          if (errorText.trim()) {
            try {
              const errorBody = JSON.parse(errorText);
              errorMessage = errorBody.error?.message || errorBody.message || errorMessage;
            } catch {
              errorMessage = `Legacy edge error (${response.status})`;
            }
          }
        } catch {
          // ignore
        }

        const errorResult: ApiV2Result<TResult> = {
          ok: false,
          code: errorCode,
          error: errorMessage,
          traceId,
          details: { status: response.status, attempt: attempt + 1, act: adminAct },
        };

        if (attempt < retryCount && this.shouldRetryError(errorCode, response.status)) {
          lastError = errorResult;
          continue;
        }

        return errorResult;
      } catch (error: any) {
        const isAborted = error?.name === 'AbortError';
        const errorCode = isAborted ? 'TIMEOUT' : 'NETWORK_ERROR';
        const errorMessage = isAborted ? 'Request timed out' : (error?.message || 'Network request failed');

        const errorResult: ApiV2Result<TResult> = {
          ok: false,
          code: errorCode,
          error: errorMessage,
          traceId,
          details: { attempt: attempt + 1, act: adminAct, originalError: error?.name },
        };

        if (attempt < retryCount && isRetriableError(errorCode)) {
          lastError = errorResult;
          continue;
        }

        return errorResult;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    return (
      lastError || {
        ok: false,
        code: 'SERVER_ERROR',
        error: 'Legacy edge function request failed after retries',
        traceId,
        details: { function: functionName },
      }
    );
  }

  private shouldRetryError(errorCode: AdminApiErrorCode, httpStatus: number): boolean {
    // Never retry auth errors or blocked calls
    if (errorCode === 'UNAUTHORIZED' || errorCode === 'FORBIDDEN' || errorCode === 'BLOCKED_ON_PUBLIC') {
      return false;
    }
    // Retry 409 (conflict), 429 (rate limit), 5xx, network errors
    return httpStatus === 409 ||
      httpStatus === 429 ||
      httpStatus >= 500 ||
      isRetriableError(errorCode);
  }

  private async callSupabaseRpc<TResult>(
    functionName: string,
    payload: Record<string, unknown>,
    traceId: string
  ): Promise<ApiV2Result<TResult>> {
    try {
      const { data, error } = await supabase.rpc<TResult>(functionName as any, payload as any);

      if (error) {
        return {
          ok: false,
          code: 'SERVER_ERROR',
          error: error.message || 'RPC call failed',
          traceId,
          details: { code: error.code, hint: error.hint }
        };
      }

      return {
        ok: true,
        data: data as TResult,
        traceId,
      };
    } catch (err: any) {
      return {
        ok: false,
        code: 'NETWORK_ERROR',
        error: err?.message || 'RPC call failed',
        traceId,
        details: { originalError: err?.name ?? 'unknown' },
      };
    }
  }

  private async callWebhooksPublic<TResult>(
    endpoint: string,
    payload: Record<string, unknown>
  ): Promise<ApiV2Result<TResult>> {
    const traceId = generateTraceId();

    try {
      // Convert payload to URL params for GET request
      const params: Record<string, string> = {};
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params[key] = String(value);
        }
      });

      const result = await webhooksPublicClient.call<TResult>(endpoint, params);

      if (result.ok) {
        return {
          ok: true,
          data: result.data!,
          traceId
        };
      } else {
        return {
          ok: false,
          code: 'SERVER_ERROR',
          error: result.error?.message || 'Webhooks API call failed',
          traceId,
          details: { endpoint, errorCode: result.error?.code }
        };
      }
    } catch (error: any) {
      return {
        ok: false,
        code: 'NETWORK_ERROR',
        error: error.message || 'Webhooks API network error',
        traceId,
        details: { endpoint }
      };
    }
  }
}

// Single instance for app-wide use
export const adminApiV2 = new AdminApiClientV2();